import { get, cloneDeep } from "lodash";
import { createSelector } from "reselect";

import { AT_TYPES } from "../../../constants/entity.js";
import { OPERATIONS } from "../../../constants/operations.js";

import { sizesQuery } from "../selectorQueries.js";
import { permissionsQuery } from "../endpoints/endpointSelector.js";
import operationSelector from "../operations/operationSelector.js";

import { getOperation } from "../../../utils/operations.js";
import {
  generateSimpleResourceMap,
  regionIdFromPropsData,
  typeKeyFromPropsData
} from "../selectorUtils.js";

// -- PROPS QUERIES -- //

const typeFromProps = props => {
  const { type } = props.match.params;
  return type;
};

const resourceIdFromProps = props => {
  const { id, type } = props.match.params;

  if (!id || !type) throw `missing property - id:${id} type:${type}`;

  return `/${type}/${id}`;
};

const operationsFromPropsQuery = (state, props) =>
  get(props, "entity.operations", null);

const enabledOperationsFromPropsQuery = (state, props) =>
  get(props, "entity.enabledOperations", null);

const sizeIdFromFormikPropsQuery = (state, props) => {
  if (!get(props, "formikProps", null) || !get(props, "id", null)) {
    // console.warn(
    //   "projectWizardSelectors: sizeIdFromFormikPropsQuery will always return null if props.formikProps or props.id is not set"
    // );
  }

  return get(props, `formikProps.values.size`, null);
};

// -- HELPERS -- //

const buildFullResource = (resource, state) => {
  if (!resource) return null;

  const id = resource["@id"];

  // create duplicate of selected resource obj
  // stringify duplication method will work as all data is in JSON-LD format
  let result = JSON.parse(JSON.stringify(resource));

  // join with other loaded entities
  const service = get(
    state,
    `services.dataMap.${resource.service}`,
    resource.service
  );

  let serviceProvider = undefined;

  if (service && service.provider) {
    serviceProvider = get(
      state,
      `providers.dataMap.${service.provider}`,
      service.provider
    );
  }

  result.provider = serviceProvider;

  result.region = get(
    state,
    `regions.dataMap.${resource.region}`,
    resource.region
  );

  result.zone = get(state, `zones.dataMap.${resource.zone}`, resource.zone);
  result.image = get(state, `images.dataMap.${resource.image}`, resource.image);
  result.size = get(state, `sizes.dataMap.${resource.size}`, resource.size);

  // optional values
  // omission from the original entity is a permissions decision so don't alter that
  const charge = get(state, `charge.dataMap.${id}`, null);
  if (charge) result.charge = charge;

  const usage = get(state, `usage.dataMap.${id}`, null);
  if (usage) result.usage = usage;

  const measurements = get(state, `measurements.dataMap.${id}`, null);
  if (measurements) result.measurements = measurements;

  return result;
};

const buildFieldOptionsFromOperations = (
  fieldName,
  operations,
  enabledOperations,
  permissions
) => {
  const operation = getOperation(
    OPERATIONS.ADD_USER,
    operations,
    enabledOperations,
    permissions
  );

  if (!operation || !operation.fields) return null;

  const field = operation.fields.find(field => field.name === fieldName);

  if (!field || !field.options) return null;

  const options = field.options.map(option => {
    return {
      key: option.value,
      text: option.text,
      value: option.value,
      disabled: option.disabled
    };
  });

  return options;
};

// -- QUERIES -- //

const genericResourceQuery = (state, props) => {
  const id = resourceSelector.resourceIdFromProps(props);
  const type = resourceSelector.typeFromProps(props);
  const resource = get(state, `${type}.dataMap.${id}`, null);

  return resourceSelector.buildFullResource(resource, state);
};

const genericIsFetchingQuery = (state, props) => {
  const type = resourceSelector.typeFromProps(props);
  return get(state, `${type}.isFetching`, null);
};

const genericIsErroredQuery = (state, props) => {
  const type = resourceSelector.typeFromProps(props);
  return get(state, `${type}.isErrored`, null);
};

// wrapper selector so it can be exported as part of the resourceSelector object
export const selectResource = () => {
  return createSelector(
    [resourceSelector.genericResourceQuery, operationSelector.permissionsQuery],
    (resource, permissions) => {
      if (!resource) return null;

      const resourceClone = cloneDeep(resource);

      return operationSelector.stampOperationPermissions(
        resourceClone,
        permissions
      );
    }
  );
};

export const selectSizesList = createSelector(
  [sizesQuery, regionIdFromPropsData, typeKeyFromPropsData],
  (sizes, regionId, typeKey) => {
    const atType = AT_TYPES.SIZES[typeKey];

    // remove readonly sizes
    let filteredSizes = sizes.filter(item => {
      if (item["@type"] === atType) {
        // initially present to implement sunset functionality
        return !item.readonly;
      }
    });

    if (regionId) {
      filteredSizes = filteredSizes.filter(item =>
        item.regions.some(element => element === regionId)
      );
    }

    if (filteredSizes) {
      return generateSimpleResourceMap(filteredSizes);
    }
    return [];
  }
);

export const selectSize = createSelector(
  [sizesQuery, sizeIdFromFormikPropsQuery],
  (sizes, sizeId) => {
    if (!sizes || !sizeId) return {};

    const size = sizes.find(item => item["@id"] === sizeId);

    return size || {};
  }
);

const selectIsFetching = () => {
  return createSelector(
    [resourceSelector.genericIsFetchingQuery],
    isFetching => isFetching
  );
};

const selectIsErrored = () => {
  return createSelector(
    [resourceSelector.genericIsErroredQuery],
    isErrored => isErrored
  );
};

export const selectProjectRoleOptions = createSelector(
  [operationsFromPropsQuery, enabledOperationsFromPropsQuery, permissionsQuery],
  (operations, enabledOperations, permissions) => {
    if (!operations || !enabledOperations || !permissions) return null;

    return buildFieldOptionsFromOperations(
      "projectGroup",
      operations,
      enabledOperations,
      permissions
    );
  }
);

export const selectSudoRoleOptions = createSelector(
  [operationsFromPropsQuery, enabledOperationsFromPropsQuery, permissionsQuery],
  (operations, enabledOperations, permissions) => {
    if (!operations || !enabledOperations || !permissions) return null;

    return buildFieldOptionsFromOperations(
      "sudo",
      operations,
      enabledOperations,
      permissions
    );
  }
);

// using the following export pattern so we can mock more easily using jest

const resourceSelector = {
  typeFromProps,
  resourceIdFromProps,
  buildFullResource,
  genericResourceQuery,
  genericIsFetchingQuery,
  genericIsErroredQuery,
  selectResource,
  selectSizesList,
  selectIsFetching,
  selectIsErrored,
  selectProjectRoleOptions,
  selectSudoRoleOptions
};

export default resourceSelector;
